import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { User } from 'lucide-react';
import { ContainerTimelineList } from '../ContainerTimelineList';

// Event Logs Tab — unified container timeline on top + focused dispatch-only
// table below. Pure presentational; parent supplies both data streams.
export const EventLogsTab = ({
  eventLogs,
  formatDateTime,
  timelineEvents,
  timelineLoading,
  timelineError,
}) => (
  <div className="space-y-8">
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Container Timeline</h3>
        <Badge variant="outline" className="text-slate-400">
          {timelineEvents?.length || 0} events
        </Badge>
      </div>
      <ContainerTimelineList
        events={timelineEvents || []}
        loading={timelineLoading}
        error={timelineError}
        emptyMessage="No timeline events yet."
      />
    </div>

    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Dispatch Event Logs</h3>
        <Badge variant="outline" className="text-slate-400">
          {eventLogs.length} dispatches
        </Badge>
      </div>

      <Table className="data-table">
        <TableHeader>
          <TableRow className="border-slate-800">
            <TableHead className="text-slate-400">Date/Time</TableHead>
            <TableHead className="text-slate-400">Task</TableHead>
            <TableHead className="text-slate-400">Driver</TableHead>
            <TableHead className="text-slate-400">Destination</TableHead>
            <TableHead className="text-slate-400">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {eventLogs.length === 0 ? (
            <TableRow className="border-slate-800">
              <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                No dispatch logs available. Dispatches sent from the Dispatch Schedule tab will appear here.
              </TableCell>
            </TableRow>
          ) : (
            eventLogs.map((log, index) => (
              <TableRow key={log.id || index} className="border-slate-800">
                <TableCell className="text-slate-300 text-xs">
                  <div className="font-mono">{formatDateTime(log.created_at)}</div>
                  {log.dispatch_date && (
                    <div className="text-slate-500 text-[10px]">
                      Scheduled: {log.dispatch_date} {log.dispatch_time_from && `${log.dispatch_time_from}`}{log.dispatch_time_to && ` - ${log.dispatch_time_to}`}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs text-blue-400 border-blue-500/50">
                    {log.task_type?.replace(/_/g, ' ') || 'DISPATCH'}
                  </Badge>
                  {log.task && (
                    <div className="text-slate-400 text-xs mt-1 max-w-[200px] truncate" title={log.task}>
                      {log.task}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-slate-300">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-500" />
                    {log.driver_name || 'Unknown'}
                  </div>
                </TableCell>
                <TableCell className="text-white max-w-[150px]">
                  <div className="truncate" title={log.destination}>
                    {log.destination || '-'}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      log.status === 'completed' ? 'text-green-400 border-green-500/50' :
                      log.status === 'accepted' ? 'text-blue-400 border-blue-500/50' :
                      log.status === 'declined' ? 'text-red-400 border-red-500/50' :
                      log.status === 'started' ? 'text-amber-400 border-amber-500/50' :
                      'text-slate-400 border-slate-500/50'
                    }`}
                  >
                    {log.status?.toUpperCase() || 'SENT'}
                  </Badge>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <p className="text-sm text-slate-500">
        Showing {eventLogs.length} dispatch event{eventLogs.length !== 1 ? 's' : ''}
      </p>
    </div>
  </div>
);
